import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import throttle from 'lodash/throttle'

import { useSpacingSubotStickyBar } from 'hooks/useSpacingSubotStickyBar'
import { fastdom } from 'utils/fastdom'

import { StyledMobileBottomNavigation } from './index.styled'

type Props = {
  children: ReactNode
  displayAlways?: boolean
  height?: number
  onScroll?: () => void
  style?: CSSProperties
}

const Wrapper = ({
  children,
  displayAlways,
  height = 44,
  onScroll,
  style,
}: Props) => {
  const bottomNavRef = useRef<HTMLDivElement | null>(null)
  const previousScrollY = useRef(0)
  const isProcessingScroll = useRef(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  useSpacingSubotStickyBar()

  const handleScroll = useCallback(
    throttle(() => {
      // Prevent concurrent scroll processing
      if (isProcessingScroll.current) return
      isProcessingScroll.current = true

      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        fastdom.measure(() => {
          onScroll?.()
          if (!bottomNavRef.current) {
            isProcessingScroll.current = false
            return
          }

          const COLLAPSE_THRESHOLD = 30
          const currentScrollY = window.scrollY
          const rect = bottomNavRef.current.getBoundingClientRect()

          let shouldCollapse = isCollapsed

          if (
            currentScrollY > previousScrollY.current + COLLAPSE_THRESHOLD &&
            currentScrollY > rect.height + 20 &&
            !isCollapsed
          ) {
            shouldCollapse = true
          }

          if (
            currentScrollY < previousScrollY.current - COLLAPSE_THRESHOLD &&
            isCollapsed
          ) {
            shouldCollapse = false
          }

          if (currentScrollY >= 0) {
            previousScrollY.current = currentScrollY
          }

          // Batch DOM updates
          if (shouldCollapse !== isCollapsed) {
            fastdom.mutate(() => {
              setIsCollapsed(shouldCollapse)
              isProcessingScroll.current = false
            })
          } else {
            isProcessingScroll.current = false
          }
        })
      })
    }, 150), // Slightly increased throttle for better performance
    [isCollapsed, onScroll],
  )

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      isProcessingScroll.current = false
    }
  }, [handleScroll])

  useEffect(() => {
    const htmlEl = document.querySelector('html')
    if (!htmlEl) return

    fastdom.mutate(() => {
      htmlEl.style.setProperty(
        '--mobile-bottom-height',
        isCollapsed ? '0px' : `${height}px`,
      )
    })

    return () => {
      fastdom.mutate(() => {
        htmlEl.style.removeProperty('--mobile-bottom-height')
      })
    }
  }, [isCollapsed, height])

  return (
    <StyledMobileBottomNavigation
      refObject={bottomNavRef}
      isCollapsed={displayAlways ? false : isCollapsed}
      data-abcxyz={true}
      height={height}
      style={style}
    >
      {children}
    </StyledMobileBottomNavigation>
  )
}

export default Wrapper
